import { ClipTranscriptSegment } from '@src/editor/models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';
import { computeCoverCrop } from '@src/editor/models/layout/CropTransformUtils';
import { SceneService } from '@src/services/SceneService';
import { findIntroCardEndTime } from '@src/utility/extraElementData';
import { VideoEditorLogger } from '@src/utility/VideoEditorLogger';

import { AbstractCommand } from '../AbstractCommand';
import { CreateScenesForRestoredRegionCommand } from '../CreateScenesForRestoredRegionCommand';
import { TimelineMode } from '../TimelineMode';

import type { TranscriptTrackManager } from '@src/editor/lib/TranscriptTrackManager';
import type { Track } from '@src/editor/models';
import type { HasClipTranscriptSegment } from '@src/editor/models/elements/HasClipTranscriptSegment';
import type { TranscriptTrack } from '@src/editor/models/TranscriptTrack';
import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
  ElementResizeResult,
} from '@src/editor/types';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type {
  FaceDetection,
  ISceneFramingService,
} from '@src/services/framing/SmartFramingService';
import type {
  ExtendEndInfo,
  ExtendStartInfo,
  MergeInfo,
} from '@src/services/SceneService';

type OperationType =
  | 'merge'
  | 'extendPrev'
  | 'extendNext'
  | 'insert'
  | 'restorePunctuation'
  | 'none';

/**
 * TranscriptSimpleRestoreCommand – restores a contiguous range of transcript
 * words (specified by their original `initial_index` values).  It looks at
 * the neighbouring VideoElements (if any) to determine the minimal timeline
 * operation required:
 *   • merge  – when the range lies exactly between two adjacent clips
 *   • extendPrev – when the range sits immediately after the previous clip
 *   • extendNext – when the range sits immediately before the next clip
 *   • insert – when no adjacent clip touches the range (paste anywhere)
 *
 * The command stores a snapshot of the entire transcript track before
 * execution, allowing undo to simply restore that snapshot.
 */
export class TranscriptRestoreCommand extends AbstractCommand {
  getName(): string {
    return 'TranscriptRestoreCommand';
  }

  private readonly fromClipSegmentId: string;
  private readonly toClipSegmentId: string;

  private readonly fromIndex: number; // 0 based index
  private readonly toIndex: number; // inclusive

  private transcriptTrack: TranscriptTrack | null = null;
  private readonly transcriptTrackManager: TranscriptTrackManager;

  private transcriptTrackElements: readonly HasClipTranscriptSegment[] = [];

  // private readonly navigator: TranscriptNavigator;

  // (removed unused undo snapshot & insert command state)

  private timelineSegmentAdded: ElementResizeResult | null = null;

  // Scene adjustment
  private readonly sceneService = new SceneService();
  private extendStartInfo: ExtendStartInfo | null = null;
  private extendEndInfo: ExtendEndInfo | null = null;
  private mergeInfoPartial: Omit<
    MergeInfo,
    'mergedElementId' | 'mergedMediaStart' | 'mergedMediaEnd'
  > | null = null;

  private readonly naturalMediaDimensions:
    | Record<string, NaturalDimensions>
    | undefined;

  private readonly sceneDetections: SceneDetection[] | undefined;
  private readonly faceDetections: FaceDetection[] | undefined;
  private readonly framingService: ISceneFramingService | undefined;
  private createScenesSubCommand: CreateScenesForRestoredRegionCommand | null =
    null;

  // Scene-boundary snap (PLAT-6599 follow-up) — REDUCES the restored range
  // toward a nearby scene-detection boundary so that sliver scenes don't get
  // created at the region's seams.  Counterpart to TranscriptRemoveCommand's
  // outward-extending snap.
  private readonly sceneSnapThreshold: number;

  // Centralised cut media times — populated by computeRestoredRangeMediaTimes(),
  // optionally snapped inward by adjustRestoredRangeToSceneBoundaries(), then
  // consumed by the apply methods (extendPrev / extendNext / performInsert).
  private newMediaEndForExtendPrev: number | null = null;
  private newMediaStartForExtendNext: number | null = null;
  private insertMediaStart: number | null = null;
  private insertMediaEnd: number | null = null;

  constructor(
    editor: VideoEditor,
    fromClipSegmentId: string,
    fromIndex: number,
    toClipSegmentId: string,
    toIndex: number,
    naturalMediaDimensions?: Record<string, NaturalDimensions>,
    sceneDetections?: SceneDetection[],
    faceDetections?: FaceDetection[],
    framingService?: ISceneFramingService,
    sceneSnapThreshold: number = 0,
  ) {
    super(editor);
    this.fromClipSegmentId = fromClipSegmentId;
    this.toClipSegmentId = toClipSegmentId;
    this.fromIndex = fromIndex;
    this.toIndex = toIndex;
    this.naturalMediaDimensions = naturalMediaDimensions;
    this.sceneDetections = sceneDetections;
    this.faceDetections = faceDetections;
    this.framingService = framingService;
    this.sceneSnapThreshold = Math.max(0, sceneSnapThreshold);

    // this.navigator = new TranscriptNavigator(this.transcript, editor);

    this.transcriptTrackManager = this.editor.getTranscriptTrackManager();

    this.transcriptTrack = editor.getVideoTranscriptTrack()!;
    this.transcriptTrackElements =
      this.transcriptTrack?.transcriptSegmentElements;
  }

  // -------------------------------------------------------------------
  // Command lifecycle
  // -------------------------------------------------------------------

  execute(): boolean {
    const track = this.transcriptTrack;
    if (!track) {
      return false;
    }

    if (this.fromClipSegmentId !== this.toClipSegmentId) {
      console.error(
        'TranscriptSimpleRestoreCommand: start and end elements should belong to the same redline segment - fromClipSegmentId and toClipSegmentId must be the same',
      );
      return false;
    }

    const { prev, next } = this.lookupNeighbours(this.fromClipSegmentId);
    const op = this.determineOperation(prev, next);

    // Pre-compute canonical restored-range media times once; then optionally
    // pull each edge INWARD toward a nearby scene-detection boundary to avoid
    // creating sliver scenes at the region's seams.
    this.computeRestoredRangeMediaTimes(op, prev, next);
    this.adjustRestoredRangeToSceneBoundaries(op, prev, next);

    switch (op) {
      case 'merge':
        if (!prev || !next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev or next for merge operation',
          );
          break;
        }
        this.applyMerge(prev, next);
        break;
      case 'extendPrev':
        if (!prev) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing prev for extendPrev operation',
          );
          break;
        }
        this.extendPrev(prev);
        break;
      case 'extendNext':
        if (!next) {
          console.error(
            'TranscriptSimpleRestoreCommand: Missing next for extendNext operation',
          );
          break;
        }
        this.extendNext(next);
        break;
      case 'insert':
        this.performInsert(prev, next);
        break;
      case 'restorePunctuation':
        this.applyRestorePunctuation();
        break;
      case 'none':
        return false;
    }

    // Recalculate timeline duration and reconcile dependent tracks
    this.recalculateTimeline();
    this.editor.markDirty('transcript_simple_restore');

    return true;
  }

  undo(): boolean {
    throw new Error('Method not implemented.');
  }

  getDescription(): string {
    return `Simple restore words (${this.fromClipSegmentId}-#${this.fromIndex}-${this.toClipSegmentId}-#${this.toIndex})`;
  }

  getTimelineSegmentAdded() {
    return this.timelineSegmentAdded;
  }

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  private getTranscriptTrack(): Track | null {
    if (this.transcriptTrack) return this.transcriptTrack;
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) return null;
    this.transcriptTrack = track;
    return track;
  }

  private lookupNeighbours(clipSegmentId: string): {
    prev: VideoTranscriptElement | null;
    next: VideoTranscriptElement | null;
  } {
    // Find adjacent timeline elements in a single call closest to
    // the fromClipSegmentId and toClipSegmentId
    const trackElementIndex = this.transcriptTrackElements.findIndex(
      el => el.getClipTranscriptSegment().id === clipSegmentId,
    );

    const prevVideoTranscriptElement = this.transcriptTrackElements
      .filter(
        (el, index) =>
          index < trackElementIndex && el instanceof VideoTranscriptElement,
      )
      .pop() as VideoTranscriptElement | null;

    const nextVideoTranscriptElement = this.transcriptTrackElements.find(
      (el, index) =>
        index > trackElementIndex && el instanceof VideoTranscriptElement,
    ) as VideoTranscriptElement | null;

    return {
      prev: prevVideoTranscriptElement,
      next: nextVideoTranscriptElement,
    };
  }

  private determineOperation(
    prev: VideoTranscriptElement | null,
    next: VideoTranscriptElement | null,
  ): OperationType {
    // Check if this is a punctuation-only restoration in a VideoTranscriptElement
    const segment = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    // Only allow punctuation restoration in VideoTranscriptElement (not RemovedTranscriptClip)
    if (segment && segment instanceof VideoTranscriptElement) {
      const clipSegment = segment.getClipTranscriptSegment();
      const wordsWithPunctuation = clipSegment.getWordsWithPunctuation();

      // Check if range contains only punctuation tokens
      const tokensInRange = wordsWithPunctuation.filter(
        (_, idx) => idx >= this.fromIndex && idx <= this.toIndex,
      );
      const isPunctuationOnly = tokensInRange.every(
        token => token.type === 'punct',
      );

      if (isPunctuationOnly && tokensInRange.length > 0) {
        return 'restorePunctuation';
      }
    }

    const prevContiguous =
      prev &&
      !prev.getClipTranscriptSegment().hasWordsAdded &&
      this.restoresStartOfRange()
        ? true
        : false;
    const nextContiguous =
      next &&
      !next.getClipTranscriptSegment().hasWordsAdded &&
      this.restoresEndOfRange()
        ? true
        : false;

    const restoresSomeWords = this.getSegmentWordsToRestore().length > 0;

    if (!restoresSomeWords) return 'none';
    if (prevContiguous && nextContiguous) return 'merge';
    if (prevContiguous) return 'extendPrev';
    if (nextContiguous) return 'extendNext';
    if (!prev && !next) return 'insert';
    // Could insert between prev and next if gap, treat as insert
    return 'insert';
  }

  /**
   * Checks if restored part includes the first word of the segment
   * meaning there are no other words after the previous video element
   */
  private restoresStartOfRange(): boolean {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();

    const wordsWithPunctuation =
      trackElementSegment?.getWordsWithPunctuation() || [];

    // no text before this.fromIndex
    return (
      wordsWithPunctuation.findIndex(
        (el, idx) => el.type === 'text' && idx < this.fromIndex,
      ) === -1
    );
  }

  /**
   * Checks if restored part includes the last word of the segment
   * meaning there are no other words before the next video element
   */
  private restoresEndOfRange(): boolean {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.toClipSegmentId)
      ?.getClipTranscriptSegment();

    const wordsWithPunctuation =
      trackElementSegment?.getWordsWithPunctuation() || [];

    // no text after this.toIndex
    return (
      wordsWithPunctuation.findIndex(
        (el, idx) => el.type === 'text' && idx > this.toIndex,
      ) === -1
    );
  }

  private applyMerge(
    prev: VideoTranscriptElement,
    next: VideoTranscriptElement,
  ): void {
    // Merge and rely on TrackManager's ripple handling
    const track = this.transcriptTrack;
    if (!track) return;

    // Capture merge info BEFORE merging (for scene adjustment)
    this.mergeInfoPartial = {
      firstElementId: prev.id,
      firstMediaStart: prev.getMediaStartTime(),
      firstMediaEnd: prev.getMediaEndTime(),
      secondElementId: next.id,
      secondMediaStart: next.getMediaStartTime(),
      secondMediaEnd: next.getMediaEndTime(),
    };

    // store original states in snapshot already, so just merge
    const mergedElement =
      this.transcriptTrackManager.bridgeAndMergeTranscriptSegmentElements(
        [prev, next],
        track,
      );

    // Apply scene adjustments AFTER merging
    if (this.mergeInfoPartial && mergedElement) {
      const mergeInfo: MergeInfo = {
        ...this.mergeInfoPartial,
        mergedElementId: mergedElement.id,
        mergedMediaStart: mergedElement.getMediaStartTime(),
        mergedMediaEnd: mergedElement.getMediaEndTime(),
      };

      this.sceneService.adjustScenesAfterMerge(this.editor.timeline, mergeInfo);

      // Create scenes for the restored portion (the gap that was bridged)
      // The restored media range is from firstMediaEnd to secondMediaStart
      const crop = this.computeCoverCropForElement(mergedElement);
      this.createScenesForRestoredRegion(
        [mergedElement],
        this.mergeInfoPartial.firstMediaEnd,
        this.mergeInfoPartial.secondMediaStart,
        crop,
      );
    }
  }

  /**
   * Pre-computes the canonical media-time cut boundaries for the operation so
   * that adjustRestoredRangeToSceneBoundaries and the apply methods all share
   * a single source of truth (mirrors the centralisation in
   * TranscriptRemoveCommand.computeCutMediaTimes).
   *
   * `merge` is skipped — both ends of the restored region are pinned by the
   * neighbouring clips' media bounds and no adjustment is possible.
   */
  private computeRestoredRangeMediaTimes(
    op: OperationType,
    _prev: VideoTranscriptElement | null,
    _next: VideoTranscriptElement | null,
  ): void {
    if (op === 'merge' || op === 'restorePunctuation' || op === 'none') {
      return;
    }

    if (op === 'extendPrev') {
      const segment = this.transcriptTrackElements
        .find(el => el.getClipTranscriptSegment().id === this.toClipSegmentId)
        ?.getClipTranscriptSegment();
      const lastWord = segment
        ?.getWordsWithPunctuation()
        .filter(
          (el, idx): el is ClipTranscriptTextToken =>
            el.type === 'text' && idx <= this.toIndex,
        )
        .pop();
      if (lastWord) {
        this.newMediaEndForExtendPrev = lastWord.media_end_ts;
      }
    } else if (op === 'extendNext') {
      const segment = this.transcriptTrackElements
        .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
        ?.getClipTranscriptSegment();
      const firstWord = segment
        ?.getWordsWithPunctuation()
        .find(
          (el, idx): el is ClipTranscriptTextToken =>
            el.type === 'text' && idx >= this.fromIndex,
        );
      if (firstWord) {
        this.newMediaStartForExtendNext = firstWord.media_ts;
      }
    } else if (op === 'insert') {
      const words = this.getSegmentWordsToRestore();
      if (words.length > 0) {
        this.insertMediaStart = words[0].media_ts;
        this.insertMediaEnd = words[words.length - 1].media_end_ts;
      }
    }
  }

  /**
   * Pulls each restored-region edge INWARD toward a nearby scene-detection
   * boundary so that CreateScenesForRestoredRegionCommand doesn't end up
   * carving paper-thin "sliver" scenes at the region's seams.
   *
   * Counterpart to TranscriptRemoveCommand.adjustCutsToSceneBoundaries, but
   * the direction is reversed: remove EXTENDS the removed range outward,
   * restore REDUCES the restored range inward.
   *
   * `merge` and `restorePunctuation` are skipped — `merge`'s two ends are
   * pinned by neighbouring clips, and `restorePunctuation` has no media cut.
   *
   * Word allocation can shift if the snap crosses a (very short) word's
   * centre.  Logged informationally — matches the precedent set by
   * TranscriptRemoveCommand (downstream paths are tolerant).
   */
  private adjustRestoredRangeToSceneBoundaries(
    op: OperationType,
    prev: VideoTranscriptElement | null,
    next: VideoTranscriptElement | null,
  ): void {
    if (this.sceneSnapThreshold === 0) return;
    if (!this.sceneDetections || this.sceneDetections.length === 0) return;
    if (op === 'merge' || op === 'restorePunctuation' || op === 'none') return;

    const timeline = this.editor.timeline;
    const snapDistance = this.sceneSnapThreshold * (1 / timeline.framerate);

    // Build the candidate set inline — duplicating the small loop from
    // CreateScenesForRestoredRegionCommand.findOverlappingDetections rather
    // than extracting a shared utility (the two consumers have slightly
    // different needs — see the plan doc).
    type DetectionBoundary = {
      start: number; // detection.startTime — candidate for snapping the region END backward
      end: number; // detection.startTime + detection.lengthSeconds — candidate for region START forward
    };
    const boundaries: DetectionBoundary[] = this.sceneDetections.map(d => ({
      start: d.startTime,
      end: d.startTime + d.lengthSeconds,
    }));

    /**
     * Finds the closest qualifying candidate within snapDistance, in the
     * given direction, then frame-aligns the result.
     *
     * 'forward':  pick `boundary.end` values in (target, target + snapDistance).
     *             Snapping makes that detection no longer overlap the region.
     * 'backward': pick `boundary.start` values in (target - snapDistance, target).
     */
    const snapInward = (
      target: number,
      direction: 'forward' | 'backward',
    ): number => {
      let best: number | null = null;
      let bestDist = Infinity;
      for (const b of boundaries) {
        const candidate = direction === 'forward' ? b.end : b.start;
        const dist = Math.abs(target - candidate);
        if (dist > snapDistance) continue;
        if (direction === 'forward' && candidate <= target) continue;
        if (direction === 'backward' && candidate >= target) continue;
        if (dist < bestDist) {
          best = candidate;
          bestDist = dist;
        }
      }
      // Intentionally NOT frame-aligned: scene detections carry their
      // source-media precision and may not sit on a frame boundary; rounding
      // to the nearest frame would re-introduce the mismatch the snap is
      // meant to eliminate. (Matches TranscriptRemoveCommand.)
      return best !== null ? best : target;
    };

    /**
     * Logs when reducing the range past a word's centre changes which token
     * `findFollowingTextElement` returns.  Informational only — we proceed.
     */
    const logIfWordBoundaryShifted = (
      segment: ClipTranscriptSegment | undefined,
      originalCut: number,
      adjustedCut: number,
      cutLabel: string,
    ): void => {
      if (!segment) return;
      const origRel = originalCut - segment.mediaStart;
      const adjRel = adjustedCut - segment.mediaStart;
      const origToken = segment.findFollowingTextElement(origRel);
      const adjToken = segment.findFollowingTextElement(adjRel);
      if (origToken?.id !== adjToken?.id) {
        VideoEditorLogger.logTranscriptOperation(
          'transcript_restore_command__scene_snap_excluded_extra_word',
          {
            cutLabel,
            original: originalCut,
            adjusted: adjustedCut,
            originalFollowingTokenId: origToken?.id ?? null,
            newFollowingTokenId: adjToken?.id ?? null,
          },
        );
      }
    };

    if (op === 'extendPrev' && this.newMediaEndForExtendPrev !== null && prev) {
      const original = this.newMediaEndForExtendPrev;
      const snapped = snapInward(original, 'backward');
      if (snapped !== original) {
        const originalMediaEnd = prev.getMediaEndTime();
        // Must stay strictly inside the region we are about to add: the
        // existing element ends at originalMediaEnd, the unsnapped target
        // is past it.  After snap we still need originalMediaEnd < snapped
        // (we are still ADDING something) and snapped < original (we are
        // only REDUCING the addition).
        if (snapped > originalMediaEnd && snapped < original) {
          const segment = this.transcriptTrackElements
            .find(
              el => el.getClipTranscriptSegment().id === this.toClipSegmentId,
            )
            ?.getClipTranscriptSegment();
          logIfWordBoundaryShifted(segment, original, snapped, 'extendPrev');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_restore_command__scene_snap_adjustment',
            {
              op,
              original,
              adjusted: snapped,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.newMediaEndForExtendPrev = snapped;
        }
      }
    } else if (
      op === 'extendNext' &&
      this.newMediaStartForExtendNext !== null &&
      next
    ) {
      const original = this.newMediaStartForExtendNext;
      const snapped = snapInward(original, 'forward');
      if (snapped !== original) {
        const originalMediaStart = next.getMediaStartTime();
        // After snap we still need snapped < originalMediaStart (still ADDING)
        // and snapped > original (only REDUCING).
        if (snapped > original && snapped < originalMediaStart) {
          const segment = this.transcriptTrackElements
            .find(
              el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
            )
            ?.getClipTranscriptSegment();
          logIfWordBoundaryShifted(segment, original, snapped, 'extendNext');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_restore_command__scene_snap_adjustment',
            {
              op,
              original,
              adjusted: snapped,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.newMediaStartForExtendNext = snapped;
        }
      }
    } else if (
      op === 'insert' &&
      this.insertMediaStart !== null &&
      this.insertMediaEnd !== null
    ) {
      const origStart = this.insertMediaStart;
      const origEnd = this.insertMediaEnd;
      const snappedStart = snapInward(origStart, 'forward');
      const snappedEnd = snapInward(origEnd, 'backward');

      // Joint validity: range must still produce some media to restore.
      // If snapping makes it empty/inverted, revert BOTH ends (mirrors the
      // CropTimelineCommand revert-on-invalid pattern).
      if (snappedStart < snappedEnd) {
        const segment = this.transcriptTrackElements
          .find(
            el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
          )
          ?.getClipTranscriptSegment();
        if (snappedStart !== origStart) {
          logIfWordBoundaryShifted(
            segment,
            origStart,
            snappedStart,
            'insert.start',
          );
          VideoEditorLogger.logTranscriptOperation(
            'transcript_restore_command__scene_snap_adjustment',
            {
              op,
              cut: 'start',
              original: origStart,
              adjusted: snappedStart,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.insertMediaStart = snappedStart;
        }
        if (snappedEnd !== origEnd) {
          logIfWordBoundaryShifted(segment, origEnd, snappedEnd, 'insert.end');
          VideoEditorLogger.logTranscriptOperation(
            'transcript_restore_command__scene_snap_adjustment',
            {
              op,
              cut: 'end',
              original: origEnd,
              adjusted: snappedEnd,
              threshold: this.sceneSnapThreshold,
            },
          );
          this.insertMediaEnd = snappedEnd;
        }
      }
      // Invalid joint constraint → leave both at originals.
    }
  }

  private extendPrev(prev: VideoTranscriptElement): void {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.toClipSegmentId)
      ?.getClipTranscriptSegment();
    const lastWordToRestore = trackElementSegment
      ?.getWordsWithPunctuation()
      .filter(
        (el, idx): el is ClipTranscriptTextToken =>
          el.type === 'text' && idx <= this.toIndex,
      )
      .pop();

    if (!lastWordToRestore) return;

    // Capture extend end info BEFORE modification (for scene adjustment).
    // Use the pre-computed (and possibly inward-snapped) cut media time.
    const originalMediaStart = prev.getMediaStartTime();
    const originalMediaEnd = prev.getMediaEndTime();
    const newMediaEnd =
      this.newMediaEndForExtendPrev ?? lastWordToRestore.media_end_ts;

    this.extendEndInfo = {
      elementId: prev.id,
      originalMediaStart,
      originalMediaEnd,
      newMediaEnd,
    };

    // Calculate new duration based on the difference between the element's trim_start and the (possibly snapped) end
    const newDuration = newMediaEnd - (prev.trim_start ?? 0);

    // Calculate the corresponding end time on the timeline
    const newEndTime = prev.time + newDuration;

    this.transcriptTrackManager.trimElementEnd(
      prev,
      newEndTime,
      TimelineMode.RIPPLE,
      false,
    );

    // Note: Extending end requires no scene adjustment as anchor positions remain valid
    // However, we need to create scenes for the restored portion
    const crop = this.computeCoverCropForElement(prev);
    this.createScenesForRestoredRegion(
      [prev],
      originalMediaEnd,
      newMediaEnd,
      crop,
    );
  }

  private extendNext(next: VideoTranscriptElement): void {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();
    const firstWordToRestore = trackElementSegment
      ?.getWordsWithPunctuation()
      .find(
        (el, idx): el is ClipTranscriptTextToken =>
          el.type === 'text' && idx >= this.fromIndex,
      );

    if (!firstWordToRestore) return;

    // Capture extend start info BEFORE modification (for scene adjustment).
    // Use the pre-computed (and possibly inward-snapped) cut media time.
    const originalMediaStart = next.getMediaStartTime();
    const originalMediaEnd = next.getMediaEndTime();
    const newMediaStart =
      this.newMediaStartForExtendNext ?? firstWordToRestore.media_ts;

    this.extendStartInfo = {
      elementId: next.id,
      originalMediaStart,
      originalMediaEnd,
      newMediaStart,
    };

    // Use TranscriptTrackManager to adjust the element start
    this.transcriptTrackManager.adjustTranscriptElementMediaStart(
      next,
      newMediaStart,
      TimelineMode.RIPPLE,
      false,
    );

    // Apply scene adjustments AFTER modification
    if (this.extendStartInfo) {
      this.sceneService.adjustScenesAfterExtendStart(
        this.editor.timeline,
        this.extendStartInfo,
      );

      // Create scenes for the restored portion (the prepended content)
      const crop = this.computeCoverCropForElement(next);
      this.createScenesForRestoredRegion(
        [next],
        newMediaStart,
        originalMediaStart,
        crop,
      );
    }
  }

  private performInsert(
    prev: VideoTranscriptElement | null,
    next: VideoTranscriptElement | null,
  ): void {
    const words = this.getSegmentWordsToRestore();
    if (!words.length) {
      console.error('Insert: No words to insert!');
      return;
    }

    // Calculate insertion parameters.  Use the pre-computed (and possibly
    // inward-snapped) cut media times so a single source of truth feeds both
    // the element bounds and the scene-creation call.
    const trimStart = this.insertMediaStart ?? words[0].media_ts;
    const mediaEnd = this.insertMediaEnd ?? trimStart + this.estimateDuration();
    const duration = mediaEnd - trimStart;
    let time = 0;

    if (prev) {
      time = prev.end_time;
    } else if (next) {
      time = Math.max(0, next.time - duration);
    }

    // When inserting before the first clip (no prev), ensure we don't
    // overlap with intro cards on other tracks. The intro card occupies
    // time 0..introCardEnd, so the restored content must start after it.
    if (!prev) {
      const introCardEnd = findIntroCardEndTime(
        this.editor.timeline.tracks,
        this.extraElementData.value,
      );
      if (introCardEnd > 0 && time < introCardEnd) {
        time = introCardEnd;
      }
    }

    // Create transcript segment for restoration
    const restoredTranscriptSegment = new ClipTranscriptSegment(
      this.getSegmentTokensToRestore().map(el =>
        el.type === 'text'
          ? {
              ...el,
              relative_ts: el.media_ts - trimStart,
              relative_end_ts: el.media_end_ts - trimStart,
            }
          : { ...el },
      ),
      trimStart,
      trimStart + duration,
    );

    const transcriptSegmentPosition = this.transcriptTrackElements.findIndex(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    if (transcriptSegmentPosition < 0) {
      console.error('Insert: Could not find transcript segment position');
      return;
    }

    // Use TranscriptTrackManager to handle the insertion
    const sourceElement = prev || next;
    const insertedElement =
      this.transcriptTrackManager.insertTranscriptSegmentAsVideoElement(
        restoredTranscriptSegment,
        sourceElement,
        time,
        this.transcriptTrack!,
        transcriptSegmentPosition,
        this.fromIndex,
        this.toIndex,
      );

    const crop = this.computeCoverCropForElement(sourceElement);

    // Create scene(s) for the inserted element
    // If sceneDetections are available, use the new command to create proper scenes
    // based on camera changes within the restored content
    if (this.sceneDetections && this.sceneDetections.length > 0) {
      this.createScenesForRestoredRegion(
        [insertedElement],
        trimStart,
        trimStart + duration,
        crop,
      );
    } else {
      // Fallback to single scene creation when no scene detections
      this.sceneService.createSceneForElement(
        this.editor.timeline,
        insertedElement,
        this.transcriptTrack!.id,
        {
          ...(crop ? { crop } : {}),
        },
      );
    }
  }

  /**
   * Computes a cover crop for a video element.
   * Returns a centered crop that fills the canvas while maintaining aspect ratio.
   */
  private computeCoverCropForElement(
    sourceElement: VideoTranscriptElement | null,
  ): { x: number; y: number; w: number; h: number } | null {
    if (!sourceElement || !this.naturalMediaDimensions) {
      return null;
    }

    const sourceUrl = sourceElement.source;
    const naturalDimensions = this.naturalMediaDimensions[sourceUrl];

    if (!naturalDimensions) {
      return null;
    }

    return computeCoverCrop(
      this.editor.timeline.width,
      this.editor.timeline.height,
      naturalDimensions.width,
      naturalDimensions.height,
    );
  }

  private getSegmentWordsToRestore(): ClipTranscriptTextToken[] {
    return (
      this.getSegmentTokensToRestore().filter(
        (el): el is ClipTranscriptTextToken => el.type === 'text',
      ) || []
    );
  }

  private getSegmentTokensToRestore(): ClipTranscriptToken[] {
    const trackElementSegment = this.transcriptTrackElements
      .find(el => el.getClipTranscriptSegment().id === this.fromClipSegmentId)
      ?.getClipTranscriptSegment();

    return (
      trackElementSegment
        ?.getWordsWithPunctuation()
        .slice(this.fromIndex, this.toIndex + 1) || []
    );
  }

  private estimateDuration(): number {
    const words = this.getSegmentWordsToRestore();
    if (!words.length) return 0;
    return words[words.length - 1].media_end_ts - words[0].media_ts;
  }

  private applyRestorePunctuation(): void {
    // Find the VideoTranscriptElement that contains the punctuation to restore
    const element = this.transcriptTrackElements.find(
      el => el.getClipTranscriptSegment().id === this.fromClipSegmentId,
    );

    if (!element || !(element instanceof VideoTranscriptElement)) {
      console.error(
        'RestorePunctuation: Element not found or not a VideoTranscriptElement',
      );
      return;
    }

    // Use TranscriptTrackManager to restore punctuation
    const success = this.transcriptTrackManager.restorePunctuation(
      element,
      this.fromIndex,
      this.toIndex,
    );

    if (!success) {
      console.warn('RestorePunctuation: Failed to restore punctuation');
    }
  }

  /**
   * Creates scenes for a restored region using scene detections.
   * This is called after each restore operation when sceneDetections are available.
   *
   * @param elements The elements containing the restored content
   * @param mediaStartTime Start of the restored region in media time
   * @param mediaEndTime End of the restored region in media time
   * @param crop Optional crop to apply to created scenes
   */
  private createScenesForRestoredRegion(
    elements: VideoTranscriptElement[],
    mediaStartTime: number,
    mediaEndTime: number,
    crop?: { x: number; y: number; w: number; h: number } | null,
  ): void {
    if (
      !this.sceneDetections ||
      this.sceneDetections.length === 0 ||
      !this.transcriptTrack
    ) {
      return;
    }

    // Create sub-command (don't create its own undo entry)
    this.createScenesSubCommand = new CreateScenesForRestoredRegionCommand(
      this.editor.timeline,
      this.sceneDetections,
      {
        elements,
        mediaStartTime,
        mediaEndTime,
      },
      this.transcriptTrack.id,
      false, // Don't create undo entry - parent command handles undo
      crop ?? undefined,
      this.framingService,
      this.faceDetections,
    );

    // Execute directly (not through executeCommand)
    this.createScenesSubCommand.execute();
  }
}
